import { useCallback, useId, useMemo, useRef } from "react";
import {
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconActivity,
  IconChecklist,
  IconChevronRight,
  IconCircleDot,
  IconCloudComputing,
  IconCode,
  IconDatabase,
  IconDeviceAnalytics,
  IconFlame,
  IconGitPullRequestClosed,
  IconLayoutGridAdd,
  IconPlayerPlay,
  IconRobot,
  IconShieldCheck,
  IconShip,
  IconTerminal2,
  IconTools,
  IconUserCheck,
} from "@tabler/icons-react";
import { SWIMLANES, DOC_LINKS } from "../data/deploymentSwimlane";
import { DiagramNodePill, exportSvgToPng, wrapLabel } from "./diagramHelpers";

interface NodePosition {
  label: string;
  columnId: string;
  centerX: number;
  centerY: number;
}

interface ColumnLayout {
  id: string;
  title: string;
  x: number;
  width: number;
  nodes: NodePosition[];
}

interface LaneLayout {
  id: string;
  title: string;
  subtitle: string;
  color: string;
  y: number;
  height: number;
  columns: ColumnLayout[];
}

const VIEWBOX_WIDTH = 1600;
const VIEWBOX_HEIGHT = 900;
const LANE_HEADER_HEIGHT = 110;
const LANE_GAP = 48;
const FOOTER_HEIGHT = 90;
const LANE_PADDING_X = 72;
const COLUMN_TITLE_HEIGHT = 44;
const PILL_HEIGHT = 44;
const PILL_WIDTH_RATIO = 0.72;
const NODE_GAP = 18;

const NODE_ICONS: Record<string, JSX.Element> = {
  Jira: <IconChecklist stroke={2} />,
  Requirements: <IconLayoutGridAdd stroke={2} />,
  "VS Code": <IconTerminal2 stroke={2} />,
  pnpm: <IconCode stroke={2} />,
  "Mantine UI": <IconCode stroke={2} />,
  GitHub: <IconGitPullRequestClosed stroke={2} />,
  "GitHub Actions": <IconRobot stroke={2} />,
  Vitest: <IconPlayerPlay stroke={2} />,
  ESLint: <IconShieldCheck stroke={2} />,
  "Unit Tests": <IconDeviceAnalytics stroke={2} />,
  "Quality Gates": <IconShieldCheck stroke={2} />,
  "Docker Build": <IconTools stroke={2} />,
  "Artifact Registry": <IconDatabase stroke={2} />,
  "GKE Staging": <IconCloudComputing stroke={2} />,
  Helm: <IconShip stroke={2} />,
  Ingress: <IconShip stroke={2} />,
  "Manual Approval": <IconUserCheck stroke={2} />,
  "Smoke Tests": <IconFlame stroke={2} />,
  "GKE Production": <IconCloudComputing stroke={2} />,
  "Cloud Monitoring": <IconActivity stroke={2} />,
};

const arrowColor = "#4ad7d1";
const guideColor = "#2b2e34";
const laneBackground = "#10131a";

export function DeploymentSwimlaneDiagram() {
  const svgRef = useRef<globalThis.SVGSVGElement | null>(null);
  const arrowMarkerId = useId();
  const shadowId = useId();
  const cardBg = "#0e0f12";
  const borderColor = "rgba(122, 169, 255, 0.15)";
  const textPrimary = "#e9ecef";
  const subtitleColor = "rgba(233, 236, 239, 0.75)";

  const laneLayouts = useMemo(() => {
    const availableHeight = VIEWBOX_HEIGHT - FOOTER_HEIGHT - LANE_GAP;
    const laneHeight = availableHeight / SWIMLANES.length;
    const laneTopOffset = 40;

    return SWIMLANES.map<LaneLayout>((lane, laneIndex) => {
      const y = laneTopOffset + laneIndex * (laneHeight + LANE_GAP / 2);
      const laneInnerWidth = VIEWBOX_WIDTH - LANE_PADDING_X * 2;
      const columnWidth = laneInnerWidth / lane.columns.length;
      const pillWidth = columnWidth * PILL_WIDTH_RATIO;
      const columnStartY = y + LANE_HEADER_HEIGHT + 40;

      const columns = lane.columns.map<ColumnLayout>((column, columnIndex) => {
        const x = LANE_PADDING_X + columnIndex * columnWidth;
        const nodes = column.nodes.map<NodePosition>((label, nodeIndex) => {
          const top = columnStartY + nodeIndex * (PILL_HEIGHT + NODE_GAP);
          const left = x + (columnWidth - pillWidth) / 2;
          return {
            label,
            columnId: column.id,
            centerX: left + pillWidth / 2,
            centerY: top + PILL_HEIGHT / 2,
          };
        });

        return {
          id: column.id,
          title: column.title,
          x,
          width: columnWidth,
          nodes,
        };
      });

      return {
        id: lane.id,
        title: lane.title,
        subtitle: lane.subtitle,
        color: lane.color,
        y,
        height: laneHeight,
        columns,
      };
    });
  }, []);

  const arrows = useMemo(() => {
    const paths: { key: string; d: string }[] = [];

    laneLayouts.forEach((lane, laneIndex) => {
      lane.columns.forEach((column, columnIndex) => {
        if (columnIndex === lane.columns.length - 1) {
          return;
        }

        const nextColumn = lane.columns[columnIndex + 1];
        column.nodes.forEach((node, nodeIndex) => {
          const target =
            nextColumn.nodes[Math.min(nodeIndex, nextColumn.nodes.length - 1)];
          if (!target) return;

          const midX = (node.centerX + target.centerX) / 2;
          const d = `M ${node.centerX} ${node.centerY} L ${midX} ${node.centerY} L ${midX} ${target.centerY} L ${target.centerX - PILL_HEIGHT / 4} ${target.centerY}`;
          paths.push({
            key: `${lane.id}-${column.id}-${node.label}-to-${nextColumn.id}`,
            d,
          });
        });
      });

      if (laneIndex < laneLayouts.length - 1) {
        const nextLane = laneLayouts[laneIndex + 1];
        const lastColumn = lane.columns[lane.columns.length - 1];
        const firstNextColumn = nextLane.columns[0];

        lastColumn.nodes.forEach((node) => {
          const target =
            firstNextColumn.nodes[0] ??
            firstNextColumn.nodes[firstNextColumn.nodes.length - 1];
          if (!target) return;

          const midX = node.centerX + 80;
          const midY = (node.centerY + target.centerY) / 2;
          const d = `M ${node.centerX} ${node.centerY} L ${midX} ${node.centerY} L ${midX} ${midY} L ${target.centerX} ${midY} L ${target.centerX} ${target.centerY - 4}`;
          paths.push({ key: `${lane.id}-handoff-${node.label}`, d });
        });
      }
    });

    return paths;
  }, [laneLayouts]);

  const downloadPng = useCallback(() => {
    if (svgRef.current) {
      exportSvgToPng(svgRef.current, "deployment-swimlane.png", "#0e0f12");
    }
  }, []);

  return (
    <Card
      withBorder
      radius="lg"
      padding="lg"
      shadow="sm"
      style={{ backgroundColor: cardBg, borderColor }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Title order={3} c={textPrimary}>
              Deployment Swimlane
            </Title>
            <Text size="sm" c={subtitleColor}>
              CI ➜ CD lifecycle overview
            </Text>
          </Group>
          <Tooltip label="Export a high-resolution PNG" withinPortal>
            <Button
              variant="light"
              leftSection={<IconChevronRight size={16} />}
              onClick={downloadPng}
            >
              Export PNG
            </Button>
          </Tooltip>
        </Group>

        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="Deployment swimlane diagram"
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <defs>
            <marker
              id={arrowMarkerId}
              markerWidth="14"
              markerHeight="14"
              refX="10"
              refY="7"
              orient="auto"
            >
              <path d="M0,0 L0,14 L12,7 z" fill={arrowColor} />
            </marker>
            <filter id={shadowId} x="-10%" y="-10%" width="130%" height="140%">
              <feDropShadow
                dx="0"
                dy="4"
                stdDeviation="6"
                floodColor="#000000"
                floodOpacity="0.35"
              />
            </filter>
          </defs>

          <rect
            width={VIEWBOX_WIDTH}
            height={VIEWBOX_HEIGHT}
            fill="#0e0f12"
            rx={32}
          />

          {laneLayouts.map((lane) => {
            const headerY = lane.y;
            const headerTitleY = headerY + 56;
            const bodyY = headerY + LANE_HEADER_HEIGHT;
            const laneBottom = bodyY + lane.height - LANE_HEADER_HEIGHT - 60;

            return (
              <g key={lane.id}>
                <rect
                  x={LANE_PADDING_X / 2}
                  y={lane.y}
                  width={VIEWBOX_WIDTH - LANE_PADDING_X}
                  height={lane.height}
                  fill={laneBackground}
                  stroke={guideColor}
                  strokeWidth={1}
                  opacity={0.65}
                  rx={24}
                />
                <rect
                  x={LANE_PADDING_X / 2}
                  y={lane.y}
                  width={VIEWBOX_WIDTH - LANE_PADDING_X}
                  height={LANE_HEADER_HEIGHT}
                  fill={lane.color}
                  opacity={0.2}
                  rx={24}
                />
                <text
                  x={LANE_PADDING_X}
                  y={headerTitleY}
                  fill={textPrimary}
                  fontSize={28}
                  fontWeight={700}
                >
                  {lane.title}
                </text>
                <text
                  x={LANE_PADDING_X}
                  y={headerTitleY + 30}
                  fill={subtitleColor}
                  fontSize={16}
                >
                  {lane.subtitle}
                </text>

                <line
                  x1={LANE_PADDING_X / 2}
                  x2={VIEWBOX_WIDTH - LANE_PADDING_X / 2}
                  y1={bodyY - 8}
                  y2={bodyY - 8}
                  stroke={guideColor}
                  strokeDasharray="6 8"
                  opacity={0.6}
                />

                {lane.columns.map((column) => {
                  const columnCenter = column.x + column.width / 2;
                  return (
                    <g key={`${lane.id}-${column.id}`}>
                      <line
                        x1={columnCenter}
                        x2={columnCenter}
                        y1={bodyY}
                        y2={laneBottom}
                        stroke={guideColor}
                        strokeDasharray="6 10"
                        opacity={0.45}
                      />
                      <text
                        x={columnCenter}
                        y={bodyY - 28}
                        fill={textPrimary}
                        fontSize={16}
                        fontWeight={600}
                        textAnchor="middle"
                      >
                        {wrapLabel(column.title, 16).map((line, idx) => (
                          <tspan
                            key={`${column.id}-${line}-${idx}`}
                            x={columnCenter}
                            dy={idx === 0 ? 0 : 18}
                          >
                            {line}
                          </tspan>
                        ))}
                      </text>

                      {column.nodes.map((node, nodeIndex) => {
                        const pillWidth = column.width * PILL_WIDTH_RATIO;
                        const left = column.x + (column.width - pillWidth) / 2;
                        const top =
                          bodyY +
                          COLUMN_TITLE_HEIGHT +
                          nodeIndex * (PILL_HEIGHT + NODE_GAP);
                        return (
                          <DiagramNodePill
                            key={`${lane.id}-${column.id}-${node.label}`}
                            x={left}
                            y={top}
                            width={pillWidth}
                            height={PILL_HEIGHT}
                            fill="rgba(22, 26, 34, 0.95)"
                            stroke="rgba(122, 169, 255, 0.4)"
                            textColor={textPrimary}
                            label={node.label}
                            icon={
                              NODE_ICONS[node.label] ?? (
                                <IconCircleDot stroke={2} />
                              )
                            }
                            href={DOC_LINKS[node.label]}
                            shadowId={shadowId}
                          />
                        );
                      })}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {arrows.map((arrow) => (
            <path
              key={arrow.key}
              d={arrow.d}
              fill="none"
              stroke={arrowColor}
              strokeWidth={3}
              strokeLinecap="round"
              markerEnd={`url(#${arrowMarkerId})`}
              opacity={0.9}
            />
          ))}

          <g
            transform={`translate(${LANE_PADDING_X}, ${VIEWBOX_HEIGHT - FOOTER_HEIGHT + 24})`}
          >
            <rect
              x={0}
              y={0}
              width={VIEWBOX_WIDTH - LANE_PADDING_X * 2}
              height={FOOTER_HEIGHT - 32}
              rx={12}
              fill="rgba(36, 43, 64, 0.8)"
              stroke="rgba(122, 169, 255, 0.3)"
            />
            <text
              x={20}
              y={(FOOTER_HEIGHT - 32) / 2 + 6}
              fill={textPrimary}
              fontSize={18}
              fontWeight={600}
            >
              Continuous Integration & Automated Testing
            </text>
            <g
              transform={`translate(${VIEWBOX_WIDTH - LANE_PADDING_X * 2 - 120}, ${(FOOTER_HEIGHT - 32) / 2 - 12})`}
            >
              {[0, 1, 2].map((index) => {
                const offset = index * 24;
                return (
                  <path
                    key={index}
                    d={`M ${offset} 0 L ${offset + 14} 12 L ${offset} 24`}
                    stroke={arrowColor}
                    strokeWidth={4}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={0.85 - index * 0.1}
                  />
                );
              })}
            </g>
          </g>

          <text
            x={VIEWBOX_WIDTH - 220}
            y={60}
            fill={subtitleColor}
            fontSize={14}
            textAnchor="end"
          >
            Click pill to open docs
          </text>
        </svg>
      </Stack>
    </Card>
  );
}
